import { useRef, useState } from 'react';
import { Button, Grid, GridItem, IconName, Body, Stack, Container } from '@optimus-web/core';
import { LocalizedText, useSafeContext } from '@mews/framework';
import { SpacedDash, SpacedForwardSlash } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { Popover } from '@mews/b2b-ui';
import { WithAvailabilityBlockId } from '@mews-commander/types';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    getResourceCategoryAdjustmentValue,
    getResourceCategoryPickedUpValue,
    isBlockAdjustmentReleased,
} from '../../../../../selectors';
import { BlockSetupMatrixVectorCellWrapper, WarningBareIconButton } from '../styles';
import { OverrideReleaseDialogContext } from '../../../../OverrideAvailabilityReleaseDialog';
import { useAvailabilityBlockInfo } from '../../../../../utils/useAvailabilityBlockInfo';

interface ReadOnlyCellProps extends WithAvailabilityBlockId {
    resourceCategoryId: string;
    dateTimeUtc: string;
    isCheckoutColumn: boolean;
    isReleaseOverriden: boolean;
    dataTestField: string;
}

const ReadOnlyCell = ({
    isCheckoutColumn,
    resourceCategoryId,
    dateTimeUtc,
    availabilityBlockId,
    isReleaseOverriden,
    dataTestField,
}: ReadOnlyCellProps) => {
    const { isConfirmed } = useAvailabilityBlockInfo(availabilityBlockId);

    const adjustedValue = useCommanderSelector((state) =>
        getResourceCategoryAdjustmentValue(state, { availabilityBlockId, dateTimeUtc, resourceCategoryId })
    );

    const pickedUpValue = useCommanderSelector((state) =>
        getResourceCategoryPickedUpValue(state, { availabilityBlockId, dateTimeUtc, resourceCategoryId })
    );

    const isAdjustmentReleased = useCommanderSelector((state) =>
        isBlockAdjustmentReleased(state, { availabilityBlockId, dateTimeUtc, resourceCategoryId })
    );

    const [isOpened, setOpened] = useState(false);
    const popoverAnchorRef = useRef<HTMLDivElement>(null);
    const { openOverrideDialog } = useSafeContext(OverrideReleaseDialogContext);

    const dataAttributes = {
        'data-test-resource-category': resourceCategoryId,
        'data-test-date-time-utc': dateTimeUtc,
        'data-test-field': dataTestField,
    };

    if (isCheckoutColumn) {
        return null;
    }

    if (!isConfirmed) {
        return (
            <BlockSetupMatrixVectorCellWrapper {...dataAttributes}>
                <Stack alignment="end" itemAlignment="center" fill>
                    <span data-test-adjustment-value>{adjustedValue}</span>
                </Stack>
            </BlockSetupMatrixVectorCellWrapper>
        );
    }

    return (
        <>
            <Popover active={isOpened} onClose={() => setOpened(false)} anchorElement={popoverAnchorRef.current}>
                <Stack vertical spacing="300" alignment="center" fill>
                    <Container maxWidth="160px">
                        <LocalizedText textKey={TextKey.AvailabilityBlockReleaseAdjustmentManuallyWarning} />
                    </Container>

                    <Button
                        onClick={() => {
                            setOpened(false);
                            openOverrideDialog({ resourceCategoryId, dateTimeUtc, isReleaseOverride: false });
                        }}
                        type="submit"
                    >
                        <LocalizedText textKey={TextKey.Release} />
                    </Button>
                </Stack>
            </Popover>
            <BlockSetupMatrixVectorCellWrapper {...dataAttributes} ref={popoverAnchorRef}>
                <Grid templateColumns="1fr auto" alignItems="center" height="100%">
                    {isReleaseOverriden && (
                        <Stack itemAlignment="center">
                            <WarningBareIconButton
                                icon={IconName.Problematic}
                                size="small"
                                onClick={() => setOpened(true)}
                            />
                        </Stack>
                    )}

                    <GridItem column="2" justifySelf="end">
                        <Stack>
                            <Body data-test-picked-up>{pickedUpValue}</Body>
                            <Body variant="secondary">
                                {SpacedForwardSlash}
                                {isAdjustmentReleased || adjustedValue === 0 ? (
                                    <span>{SpacedDash}</span>
                                ) : (
                                    <span data-test-adjustment-value>{adjustedValue}</span>
                                )}
                            </Body>
                        </Stack>
                    </GridItem>
                </Grid>
            </BlockSetupMatrixVectorCellWrapper>
        </>
    );
};

export default ReadOnlyCell;
